import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

function HealthAnalytics() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('2024-04-01');
  const [endDate, setEndDate] = useState('2024-08-07');
  const [showConcernChart, setShowConcernChart] = useState(true);
  const [showPopulationChart, setShowPopulationChart] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState('Both');
  const [presetDates, setPresetDates] = useState('Custom');

  useEffect(() => {
    const fetchEntries = async () => {
      if (!startDate || !endDate) {
        setEntries([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const start = new Date(`${startDate}T00:00:00`);
        const end = new Date(`${endDate}T23:59:59.999`);
        const studentCollection = query(
          collection(db, 'studentHealthEntries'),
          where('date', '>=', Timestamp.fromDate(start)),
          where('date', '<=', Timestamp.fromDate(end))
        );
        const staffCollection = query(
          collection(db, 'staffHealthEntries'),
          where('date', '>=', Timestamp.fromDate(start)),
          where('date', '<=', Timestamp.fromDate(end))
        );
        const [studentSnapshot, staffSnapshot] = await Promise.all([
          getDocs(studentCollection),
          getDocs(staffCollection)
        ]);

        const studentEntries = studentSnapshot.docs.map(doc => ({...doc.data(), date: doc.data().date.toDate(), type: 'student'}));
        const staffEntries = staffSnapshot.docs.map(doc => ({...doc.data(), date: doc.data().date.toDate(), type: 'staff'}));
        
        setEntries([...studentEntries, ...staffEntries]);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching health entries: ", error);
        setEntries([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEntries();
  }, [startDate, endDate]);

  useEffect(() => {
    updateDatesFromPreset(presetDates);
  }, [presetDates]);

  const updateDatesFromPreset = (preset) => {
    const today = new Date();
    let start, end;

    switch (preset) {
      case 'This Week':
        start = new Date(today.setDate(today.getDate() - today.getDay()));
        end = new Date(today.setDate(today.getDate() - today.getDay() + 6));
        break;
      case 'Last Week':
        start = new Date(today.setDate(today.getDate() - today.getDay() - 7));
        end = new Date(today.setDate(today.getDate() - today.getDay() - 1));
        break;
      case 'This Month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case 'Last Month':
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'Last Year':
        start = new Date(today.getFullYear() - 1, 0, 1);
        end = new Date(today.getFullYear() - 1, 11, 31);
        break;
      default:
        return; // Don't update for 'Custom'
    }

    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  const filterEntriesByGroup = (entries) => {
    if (selectedGroup === 'Both') return entries;
    return entries.filter(entry => entry.type.toLowerCase() === selectedGroup.toLowerCase());
  };

  const getConcernCounts = (entries) => {
    const concernCounts = {};
    entries.forEach(entry => {
      concernCounts[entry.concern] = (concernCounts[entry.concern] || 0) + 1;
    });
    return concernCounts;
  };

  const getMonthlyEntryCounts = (entries) => {
    const monthlyCounts = {};
    entries.forEach(entry => {
      const monthYear = entry.date.toLocaleString('default', { month: 'short', year: '2-digit' });
      monthlyCounts[monthYear] = (monthlyCounts[monthYear] || 0) + 1;
    });
    return monthlyCounts;
  };

  const filteredEntries = filterEntriesByGroup(entries);
  const concernCounts = getConcernCounts(filteredEntries);
  const monthlyEntryCounts = getMonthlyEntryCounts(filteredEntries);

  const concernChartData = {
    labels: Object.keys(concernCounts),
    datasets: [
      {
        data: Object.values(concernCounts),
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
      },
    ],
  };

  const populationChartData = {
    labels: Object.keys(monthlyEntryCounts),
    datasets: [
      {
        data: Object.values(monthlyEntryCounts),
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        font: {
          size: 16,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  if (loading) {
    return <div className="text-center mt-8">Loading...</div>;
  }

  return (
    <div className="p-4">
      <div className="mb-4 grid grid-cols-2 gap-4">
        <div>
          <label className="block mb-2">Start Date:</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setPresetDates('Custom');
            }}
            className="w-full p-2 border rounded"
          />
        </div>
        <div>
          <label className="block mb-2">End Date:</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setPresetDates('Custom');
            }}
            className="w-full p-2 border rounded"
          />
        </div>
      </div>

      <div className="mb-4 flex items-center space-x-4">
        <label>
          <input
            type="checkbox"
            checked={showConcernChart}
            onChange={(e) => setShowConcernChart(e.target.checked)}
            className="mr-2"
          />
          Show Concern Chart
        </label>
        <label>
          <input
            type="checkbox"
            checked={showPopulationChart}
            onChange={(e) => setShowPopulationChart(e.target.checked)}
            className="mr-2"
          />
          Show Population Chart
        </label>
      </div>

      <div className="mb-4">
        <label className="block mb-2">Select From Preset Date:</label>
        <select
          value={presetDates}
          onChange={(e) => setPresetDates(e.target.value)}
          className="w-full p-2 border rounded"
        >
          <option value="Custom">Custom</option>
          <option value="This Week">This Week</option>
          <option value="Last Week">Last Week</option>
          <option value="This Month">This Month</option>
          <option value="Last Month">Last Month</option>
          <option value="Last Year">Last Year</option>
        </select>
      </div>

      <div className="mb-4">
        <label className="block mb-2">Select who you'd like to see results for:</label>
        <div className="flex space-x-4">
          <label>
            <input
              type="radio"
              value="Student"
              checked={selectedGroup === 'Student'}
              onChange={(e) => setSelectedGroup(e.target.value)}
              className="mr-2"
            />
            Student
          </label>
          <label>
            <input
              type="radio"
              value="Staff"
              checked={selectedGroup === 'Staff'}
              onChange={(e) => setSelectedGroup(e.target.value)}
              className="mr-2"
            />
            Staff
          </label>
          <label>
            <input
              type="radio"
              value="Both"
              checked={selectedGroup === 'Both'}
              onChange={(e) => setSelectedGroup(e.target.value)}
              className="mr-2"
            />
            Both
          </label>
        </div>
      </div>

      {showConcernChart && (
        <div className="mt-8">
          <Bar
            data={concernChartData}
            options={{...chartOptions, plugins: {...chartOptions.plugins, title: {display: true, text: 'Concerns'}}}}
          />
        </div>
      )}

      {showPopulationChart && (
        <div className="mt-8">
          <Bar
            data={populationChartData}
            options={{...chartOptions, plugins: {...chartOptions.plugins, title: {display: true, text: 'Total Entries'}}}}
          />
        </div>
      )}
    </div>
  );
}

export default HealthAnalytics;
